import React, { useEffect, useState } from 'react';
import type { ToastMessage, ToastType } from '../../../contexts/ToastContext';

const ICONS = {
    success: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
    ),
    error: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
    ),
    info: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-5 h-5">
            <circle cx="12" cy="12" r="10" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4m0-4h.01" />
        </svg>
    ),
    warning: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
    ),
};

const ICON_COLORS: Record<ToastType, string> = {
    success: 'text-[#16a34a]',
    error: 'text-[#e52e2e]',
    warning: 'text-[#d97706]',
    info: 'text-[#3b82f6]',
};

interface ToastProps {
    toast: ToastMessage;
    onClose: () => void;
}

export const Toast: React.FC<ToastProps> = ({ toast, onClose }) => {
    const [mounted, setMounted] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    const duration = toast.duration ?? 3000;

    useEffect(() => {
        const enterTimer = setTimeout(() => {
            setMounted(true);
        }, 20);

        const closeTimer = setTimeout(() => {
            setIsClosing(true);
            setTimeout(onClose, 300);
        }, duration);

        return () => {
            clearTimeout(enterTimer);
            clearTimeout(closeTimer);
        };
    }, [duration, onClose]);

    let animationClasses = 'opacity-0 -translate-y-5';

    if (mounted && !isClosing) {
        animationClasses = 'opacity-100 translate-y-0';
    } else if (isClosing) {
        animationClasses = 'opacity-0 translate-y-0';
    }

    return (
        <div
            role="alert"
            className={[
                'flex items-center gap-3 bg-white rounded-xl p-[14px_18px]',
                'min-w-[280px] max-w-[400px] w-full select-none',
                'shadow-[0_8px_30px_rgba(0,0,0,0.12)] pointer-events-auto',
                'transition-[opacity,transform] duration-300 ease-out',
                'transform-gpu will-change-[transform,opacity]',
                animationClasses
            ].join(' ')}
        >
            <div className={`flex-shrink-0 w-5 h-5 ${ICON_COLORS[toast.type]}`}>
                {ICONS[toast.type]}
            </div>

            <span className="flex-1 text-sm font-medium text-[#1a1a1a] leading-[1.4] subpixel-antialiased">
                {toast.message}
            </span>
        </div>
    );
};